import { useMelioForm, UseMelioFormProps, UseMelioFormResults } from '@melio/penny';
import { CreateVendorParams } from '@melio/platform-api';
import { FormEvent } from 'react';
import { Path } from 'react-hook-form';

import { useInlineApiError } from './apiErrorsUtils';
import { AddVendorFormFields, AddVendorFormInlineApiErrorCode, AddVendorFormWidgetProps } from './types';

type UseAddVendorFormParams<T extends AddVendorFormFields> = Omit<UseMelioFormProps<T>, 'onSubmit'> & {
  inlineApiErrorCodes?: AddVendorFormInlineApiErrorCode[];
  getPostVendorPayloadFromFormValues: (data: AddVendorFormFields) => CreateVendorParams;
  onSubmit: AddVendorFormWidgetProps['onSubmit'];
};

export const useAddVendorForm = <T extends AddVendorFormFields>({
  inlineApiErrorCodes,
  getPostVendorPayloadFromFormValues,
  onSubmit: _onSubmit,
  ...useMelioFormParams
}: UseAddVendorFormParams<T>): UseMelioFormResults<T> => {
  const onSubmit: UseMelioFormProps<T>['onSubmit'] = (data, _event, target?) =>
    _onSubmit(getPostVendorPayloadFromFormValues(data), target);

  const {
    registerField: baseRegisterField,
    formState,
    formProps: baseFormProps,
    ...rest
  } = useMelioForm<T>({ onSubmit, ...useMelioFormParams });

  const { fieldToApiErrorMessageMap } = useInlineApiError<T>(inlineApiErrorCodes);

  const registerField = (field: Path<T>) => {
    const apiErrorMessage = fieldToApiErrorMessageMap[field];

    const shouldHideError = field === 'confirmAccountNumber' && formState.errors.accountNumber?.type === 'required';

    const fieldData = baseRegisterField(field);
    let error = fieldData.error;
    if (shouldHideError) {
      error = undefined;
    } else if (apiErrorMessage) {
      error = { message: apiErrorMessage, type: 'api-error' };
    }

    return {
      ...baseRegisterField(field),
      error,
    };
  };

  const formProps = {
    ...baseFormProps,
    onSubmit: (e: FormEvent) => {
      e.stopPropagation();
      baseFormProps.onSubmit(e);
    },
  };

  return { registerField, formProps, formState, ...rest };
};
